import type {
	SoilCard,
	SoilCriterion,
	SoilTexture,
	StateReferenceDTO,
	StateReferenceFixedClass,
} from '../types/parameters.types';

export interface SoilRuleForm {
	code: string;
	kind: 'fixed' | SoilCriterion;
	clayMode: 'texture' | 'range';
	texture: SoilTexture;
	minValue?: number;
	maxValue?: number;
	name: string;
	method: string;
	unit: string;
	idealRange: string;
	classes: StateReferenceFixedClass[];
}

export type SoilRuleUpdate = Pick<StateReferenceDTO, 'dependentParams'> | Pick<StateReferenceDTO, 'fixedParams'>;

type SoilRuleResult = { ok: true; update: SoilRuleUpdate } | { ok: false; error: string };

const fail = (error: string): SoilRuleResult => ({ ok: false, error });

const OVERLAP_MESSAGE = 'A faixa de aplicação se sobrepõe a uma regra já cadastrada.';

const overlaps = (
	existing: { minValue?: number; maxValue?: number; texture?: SoilTexture },
	min: number | undefined,
	max: number | undefined
) =>
	!existing.texture &&
	(min ?? -Infinity) < (existing.maxValue ?? Infinity) &&
	(existing.minValue ?? -Infinity) < (max ?? Infinity);

const identity = (form: SoilRuleForm) => ({
	name: form.name.trim(),
	unit: form.unit.trim(),
	method: form.method.trim(),
});

export const buildSoilRule = (reference: StateReferenceDTO, form: SoilRuleForm): SoilRuleResult => {
	const idealRange = form.idealRange.trim();
	const fixedExists = reference.fixedParams.some((param) => param.code === form.code);
	const dependentIndex = reference.dependentParams.findIndex((param) => param.code === form.code);

	if (form.kind === 'fixed') {
		if (fixedExists || dependentIndex >= 0) return fail('Este parâmetro já está cadastrado. Edite a regra existente.');
		return {
			ok: true,
			update: {
				fixedParams: [
					...reference.fixedParams,
					{ code: form.code, ...identity(form), idealRange, fixedClasses: form.classes },
				],
			},
		};
	}

	if (fixedExists) return fail('Este parâmetro já possui uma regra fixa. Edite a regra existente.');

	const useTexture = form.kind === 'clay' && form.clayMode === 'texture';
	const { minValue, maxValue } = form;
	const current = dependentIndex >= 0 ? reference.dependentParams[dependentIndex] : null;
	const rules = current ? current.rules.map((rule) => ({ ...rule })) : [];
	let rule = rules.find((item) => item.criterion === form.kind);
	if (!rule) {
		rule = { criterion: form.kind, dependentClasses: [] };
		rules.push(rule);
	}

	if (useTexture && rule.dependentClasses.some((item) => item.texture === form.texture)) {
		return fail('Esta textura já tem regra para o parâmetro.');
	}
	if (!useTexture && rule.dependentClasses.some((item) => overlaps(item, minValue, maxValue))) {
		return fail(OVERLAP_MESSAGE);
	}

	rule.dependentClasses = [
		...rule.dependentClasses,
		{
			idealRange,
			fixedClasses: form.classes,
			...(useTexture ? { texture: form.texture } : { minValue, maxValue }),
		},
	];

	const param = { code: form.code, ...identity(form), rules };
	const dependentParams =
		dependentIndex >= 0
			? reference.dependentParams.map((item, i) => (i === dependentIndex ? param : item))
			: [...reference.dependentParams, param];

	return { ok: true, update: { dependentParams } };
};

export const applySoilEdit = (reference: StateReferenceDTO, card: SoilCard, form: SoilRuleForm): SoilRuleResult => {
	const idealRange = form.idealRange.trim();

	if (card.kind === 'fixed') {
		const fixedParams = reference.fixedParams.map((param, i) =>
			i === card.paramPath[1] ? { ...param, ...identity(form), idealRange, fixedClasses: form.classes } : param
		);
		return { ok: true, update: { fixedParams } };
	}

	const [, paramIndex, , ruleIndex, , classIndex] = card.classPath as number[];
	const rule = reference.dependentParams[paramIndex].rules[ruleIndex];
	const others = rule.dependentClasses.filter((_, k) => k !== classIndex);

	if (card.hasBand && others.some((item) => overlaps(item, form.minValue, form.maxValue))) {
		return fail(OVERLAP_MESSAGE);
	}

	const dependentClasses = rule.dependentClasses.map((item, k) =>
		k === classIndex
			? {
					...item,
					idealRange,
					fixedClasses: form.classes,
					...(card.hasBand ? { minValue: form.minValue, maxValue: form.maxValue } : {}),
				}
			: item
	);

	const dependentParams = reference.dependentParams.map((param, i) =>
		i === paramIndex
			? {
					...param,
					...identity(form),
					rules: param.rules.map((item, j) => (j === ruleIndex ? { ...item, dependentClasses } : item)),
				}
			: param
	);

	return { ok: true, update: { dependentParams } };
};
